// Onboarding restore path via a synced passkey (docs/03 §2, WebAuthn PRF).
// The primary flow is DISCOVERABLE (usernameless): one tap, the platform's
// credential sheet offers the resident Merkury passkeys, and the assertion
// both mints the session and unwraps the identity seed — nothing typed. A
// successful ceremony reconstructs the SAME identity keypair the recovery
// phrase would, invisibly to friends. Accounts whose passkey predates the
// fixed-salt scheme can expand the legacy account-ID form (its per-user salt
// is fetched with the options and evaluated in the same ceremony).
// Feature-detected: this step is only offered where a WebAuthn platform exists.

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  classifyWebAuthnError,
  supportsWebAuthn,
  type IdentityKeys,
} from '../../identity/index.js';
import { useT, type MessageKey } from '../../i18n/index.js';

export function RestorePasskey({
  onRestore,
  onRestored,
  onBack,
  onUsePhrase,
  onPrime,
}: {
  /** Runs the PRF ceremony (discoverable when `userId` is absent); resolves
   *  the keys, or undefined when no matching passkey / no PRF output.
   *  A dismissed sheet or a WebAuthn platform error propagates as a rejection
   *  so the copy can distinguish "cancelled" from "nothing found". */
  onRestore: (userId?: string) => Promise<IdentityKeys | undefined>;
  onRestored: (identity: IdentityKeys) => void;
  onBack: () => void;
  /** Jump straight to the recovery-phrase step (the never-a-dead-end fallback).
   *  When absent, Back still returns to Welcome where the phrase path lives. */
  onUsePhrase?: () => void;
  /** Prefetch the discoverable-restore options on mount so the one-tap ceremony
   *  fires with no server round-trip before navigator.credentials.get (Safari
   *  transient activation, web/passkey-audit.md §1). Best-effort; no-op if
   *  unwired. */
  onPrime?: () => void;
}): ReactNode {
  const t = useT();
  useEffect(() => {
    onPrime?.();
  }, [onPrime]);
  const [userId, setUserId] = useState('');
  const [byId, setById] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  // Defense-in-depth: OnboardingFlow only routes here when WebAuthn exists, but
  // if the platform can't run the ceremony we say so plainly and lean on the
  // phrase fallback rather than letting the button dead-end.
  const supported = supportsWebAuthn();

  async function run(id?: string): Promise<void> {
    if (!supported) {
      setError(t('restorePasskey.unsupported'));
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const identity = await onRestore(id);
      if (identity) {
        onRestored(identity);
        return;
      }
      // Ceremony completed but yielded no restorable identity: no discoverable
      // passkey for this account on this device (or no PRF blob to unwrap).
      setError(t('restorePasskey.noCredential'));
    } catch (err) {
      const failure = classifyWebAuthnError(err);
      const key: MessageKey =
        failure === 'cancelled'
          ? 'restorePasskey.cancelled'
          : failure === 'unsupported'
            ? 'restorePasskey.unsupported'
            : 'restorePasskey.failed';
      setError(t(key));
    } finally {
      setBusy(false);
    }
  }

  function handleSubmitById(e: FormEvent): void {
    e.preventDefault();
    const id = userId.trim().toLowerCase();
    if (!id) return;
    void run(id);
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-ink">{t('restorePasskey.title')}</h1>
      <p className="text-sm text-muted">{t('restorePasskey.help')}</p>
      {!supported && (
        <p role="note" className="text-sm text-muted">
          {t('restorePasskey.unsupported')}
        </p>
      )}
      <button
        type="button"
        disabled={busy || !supported}
        onClick={() => void run()}
        className="min-h-touch rounded-xl bg-accent px-4 font-medium text-ink disabled:opacity-40 active:bg-accent-press"
      >
        {busy ? t('restorePasskey.working') : t('restorePasskey.useButton')}
      </button>
      {error !== undefined && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      {onUsePhrase && (
        <button
          type="button"
          onClick={onUsePhrase}
          disabled={busy}
          className="self-start text-sm text-accent underline disabled:opacity-40"
        >
          {t('restorePasskey.usePhrase')}
        </button>
      )}
      {byId ? (
        <form className="flex flex-col gap-4" onSubmit={handleSubmitById}>
          <label className="flex flex-col gap-1 text-sm text-ink">
            {t('restorePasskey.label')}
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="min-h-touch rounded-xl border border-edge bg-surface px-3 py-2 font-mono text-ink"
            />
          </label>
          <button
            type="submit"
            disabled={busy || userId.trim() === ''}
            className="min-h-touch rounded-xl border border-edge bg-surface px-4 font-medium text-ink disabled:opacity-40"
          >
            {busy ? t('restorePasskey.working') : t('restorePasskey.continue')}
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setById(true)}
          className="self-start text-sm text-muted underline"
        >
          {t('restorePasskey.byId')}
        </button>
      )}
      <div className="mt-auto flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={busy}
          className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink disabled:opacity-40"
        >
          {t('common.back')}
        </button>
      </div>
    </main>
  );
}
